import { useQuery } from "@tanstack/react-query";
import { authApi } from "@config/axios/axios.config";
import { SERVER_ROUTES, QUERY_KEYS } from "@constants/api.routes";
import type { ProfileData } from "../../types";

interface SectorApi {
	id: number;
	uuid: string;
	createdAt?: string;
	updatedAt?: string;
	name: string;
	slug?: string | null;
	isActive?: boolean;
	description?: string | null;
}

interface EmployerBusinessDetailsApi {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	businessName: string;
	phoneNumber: string;
	address: string;
	businessWebsiteURL: string | null;
	businessPrincipalLine: string | null;
	businessEmailAddress: string | null;
	employerType: string | null;
	certificateOfIncorporation: string | null;
	ntn: string | null;
	employerCategory: {
		id: number;
		uuid: string;
		name: string;
	} | null;
	employerCategoryId: number | null;
	sector: SectorApi | null; // Full sector object with name
	sectorId: number | null;
	// Person fields are flat, not nested
	personFullName: string | null;
	personEmailAddress: string | null;
	personLandlineNumber: string | null;
	personMobileNumber: string | null;
	personAddress: string | null;
	personCnic: string | null;
	userId: number;
}

// This is the actual user data structure from API
interface ProfileUserData {
	id: number;
	uuid: string;
	createdAt: string;
	updatedAt: string;
	firstName: string | null;
	lastName: string | null;
	username: string | null;
	emailAddress: string;
	phoneNumber: string | null;
	networkOperator: string | null;
	cnic: string | null;
	dob: string | null;
	landlineNumber: string | null;
	profilePicture: string | null;
	signature: string | null;
	isActive: boolean;
	isProfileCompleted: boolean;
	userTypeId: number;
	employerBusinessDetails?: EmployerBusinessDetailsApi;
	technologyId: number | null;
}

// Full API response structure
interface ProfileApiResponse {
	success: boolean;
	statusCode: number;
	message: string;
	data: ProfileUserData;
}

const transformApiResponse = (apiData: ProfileUserData): ProfileData => {
	const empDetails = apiData.employerBusinessDetails;
	
	return {
		id: apiData.id,
		uuid: apiData.uuid,
		emailAddress: apiData.emailAddress,
		phoneNumber: apiData.phoneNumber || undefined,
		cnic: apiData.cnic || undefined,
		profilePicture: apiData.profilePicture || undefined,
		isProfileCompleted: apiData.isProfileCompleted,
		businessDetails: empDetails ? {
			id: empDetails.id,
			uuid: empDetails.uuid,
			businessName: empDetails.businessName,
			phoneNumber: empDetails.phoneNumber,
			address: empDetails.address,
			businessWebsiteURL: empDetails.businessWebsiteURL || undefined,
			businessPrincipalLine: empDetails.businessPrincipalLine || undefined,
			businessEmailAddress: empDetails.businessEmailAddress || undefined,
			employerType: empDetails.employerType || undefined,
			ntn: empDetails.ntn || undefined,
			certificateOfIncorporation: empDetails.certificateOfIncorporation || undefined,
			employerCategory: empDetails.employerCategory ? {
				id: empDetails.employerCategory.id,
				uuid: empDetails.employerCategory.uuid,
				name: empDetails.employerCategory.name,
			} : undefined,
			sector: empDetails.sector ? {
				id: empDetails.sector.id,
				uuid: empDetails.sector.uuid,
				name: empDetails.sector.name,
			} : (empDetails.sectorId ? { id: empDetails.sectorId } : undefined),
			// Transform flat person fields to nested person object
			person: {
				fullName: empDetails.personFullName || undefined,
				email: empDetails.personEmailAddress || undefined,
				mobile: empDetails.personMobileNumber || undefined,
				landline: empDetails.personLandlineNumber || undefined,
				address: empDetails.personAddress || undefined,
				cnic: empDetails.personCnic || undefined,
			},
		} : undefined,
	};
};

export const useProfile = () => {
	const query = useQuery<ProfileData, Error>({
		queryKey: [QUERY_KEYS.USER.GET_PROFILE],
		queryFn: async (): Promise<ProfileData> => {
			// axios interceptor returns response.data, so we get the API response body directly
			// The response is {success, statusCode, message, data: ProfileUserData}
			const response = await authApi.get(SERVER_ROUTES.USER.GET_PROFILE) as ProfileApiResponse;
			console.log("Profile API response:", response);
			return transformApiResponse(response.data);
		},
		// Don't use cache - always fetch fresh data
		staleTime: 0,
		gcTime: 0,
		refetchOnMount: "always",
	});

	return {
		data: query.data,
		isLoading: query.isLoading,
		isError: query.isError,
		error: query.error,
		refetch: query.refetch,
	};
};
